import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { GpsPosition } from '@gps-hunt/shared';
import { useLocationStore } from '../stores/locationStore';

interface GeolocationState {
  position: GpsPosition | null;
  error: string | null;
  isLoading: boolean;
  isWatching: boolean;
  locationDenied: boolean;
}

interface UseGeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

const defaultOptions: UseGeolocationOptions = {
  enableHighAccuracy: true,
  timeout: 15000, // 15s - iOS can be slow to acquire GPS
  maximumAge: 0,
};

/**
 * Custom hook for geolocation tracking
 */
export function useGeolocation(options: UseGeolocationOptions = {}) {
  const [state, setState] = useState<GeolocationState>({
    position: null,
    error: null,
    isLoading: true,
    isWatching: false,
    locationDenied: false,
  });

  const watchIdRef = useRef<number | null>(null);
  const setGpsSignal = useLocationStore((s) => s.setGpsSignal);

  const opts = useMemo(
    () => ({ ...defaultOptions, ...options }),
    [options.enableHighAccuracy, options.timeout, options.maximumAge]
  );

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    const accuracy = position.coords.accuracy;
    setState((prev) => ({
      ...prev,
      position: {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
        accuracy,
        heading: position.coords.heading ?? undefined,
        speed: position.coords.speed ?? undefined,
        timestamp: position.timestamp,
      },
      error: null,
      isLoading: false,
    }));
    // Sync to global store for AppHeader
    setGpsSignal({ accuracy, isLoading: false, error: null });
  }, [setGpsSignal]);

  const handleError = useCallback((error: GeolocationPositionError) => {
    let errorMessage: string;
    let denied = false;
    switch (error.code) {
      case error.PERMISSION_DENIED:
        errorMessage = 'Location permission denied. Please enable location access.';
        denied = true;
        break;
      case error.POSITION_UNAVAILABLE:
        errorMessage = 'Location information unavailable.';
        break;
      case error.TIMEOUT:
        errorMessage = 'Location request timed out.';
        break;
      default:
        errorMessage = 'An unknown error occurred.';
    }
    setState((prev) => ({
      ...prev,
      error: errorMessage,
      isLoading: false,
      locationDenied: denied || prev.locationDenied,
    }));
    // Sync to global store
    setGpsSignal({ error: errorMessage, isLoading: false });
  }, [setGpsSignal]);

  const retryCountRef = useRef(0);
  const maxRetries = 3;

  const getCurrentPosition = useCallback(() => {
    if (!navigator.geolocation) {
      setState((prev) => ({
        ...prev,
        error: 'Geolocation is not supported by your browser.',
        isLoading: false,
      }));
      return;
    }

    setState((prev) => ({ ...prev, isLoading: true }));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        retryCountRef.current = 0; // Reset on success
        handleSuccess(pos);
      },
      (err) => {
        // Retry on timeout (common on iOS when GPS is slow to acquire)
        if (err.code === err.TIMEOUT && retryCountRef.current < maxRetries) {
          retryCountRef.current++;
          console.log(`Geolocation retry ${retryCountRef.current}/${maxRetries}`);
          navigator.geolocation.getCurrentPosition(handleSuccess, handleError, {
            ...opts,
            timeout: opts.timeout! * 1.5, // Increase timeout on retry
          });
        } else {
          handleError(err);
        }
      },
      opts
    );
  }, [handleSuccess, handleError, opts]);

  const startWatching = useCallback(() => {
    if (!navigator.geolocation) {
      const errorMsg = 'Geolocation is not supported by your browser.';
      setState((prev) => ({
        ...prev,
        error: errorMsg,
        isLoading: false,
      }));
      setGpsSignal({ error: errorMsg, isLoading: false, isWatching: false });
      return;
    }

    // Clear any existing watch
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }

    setState((prev) => ({ ...prev, isWatching: true, isLoading: true }));
    setGpsSignal({ isWatching: true, isLoading: true });

    const watchId = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      opts
    );

    watchIdRef.current = watchId;
  }, [handleSuccess, handleError, opts, setGpsSignal]);

  const stopWatching = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setState((prev) => ({ ...prev, isWatching: false }));
    setGpsSignal({ isWatching: false });
  }, [setGpsSignal]);

  // Get initial position on mount
  useEffect(() => {
    getCurrentPosition();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup watch on unmount
  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
    };
  }, []);

  return {
    ...state,
    getCurrentPosition,
    startWatching,
    stopWatching,
  };
}

/**
 * Check if GPS accuracy meets requirements (≤20m)
 */
export function isAccuracyAcceptable(accuracy: number): boolean {
  return accuracy <= 20;
}
